import React, { useEffect, useState } from "react";
import {
    Button,
    Drawer,
    Flex,
    Text,
    Title,
    Table,
    Card,
    TextInput,
    Group,
    Notification,
    Tabs,
} from "@mantine/core";
import { Organization } from "../../../models/Organization";
import { updateOrganization } from "../../../Services/OrganizationService";
import { CreateInvitation } from "../CreateInvitation";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import {promoteMembership} from "../../../Services/MembershipService";
import {MembershipRole} from "../../../models/Membership";

interface ManageOrganizationProps {
    organization: Organization;
    onOrgUpdated: () => void
}

export const ManageOrganization: React.FC<ManageOrganizationProps> = ({ organization, onOrgUpdated }) => {
    // Drawer
    const [opened, setOpened] = useState(false);

    // Success/error messages
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Mantine form for updating org
    const orgForm = useForm({
        initialValues: {
            name: "",
            street: "",
            city: "",
            state: "",
            zip: "",
            country: "",
        },
    });

    // Whenever `organization` changes, update form fields
    useEffect(() => {
        if (organization) {
            orgForm.setValues({
                name: organization.name || "",
                street: organization.street || "",
                city: organization.city || "",
                state: organization.state || "",
                zip: organization.zip || "",
                country: organization.country || "",
            });
        }
        // eslint-disable-next-line
    }, [organization]);

    // Handle update form submission
    const handleUpdateSubmit = async (values: typeof orgForm.values) => {
        if (!organization) return;

        try {
            setSuccessMessage(null);
            setErrorMessage(null);

            await updateOrganization(organization.id, {
                name: values.name,
                street: values.street,
                city: values.city,
                state: values.state,
                zip: values.zip,
                country: values.country,
            });

            setSuccessMessage("Organization updated successfully!");
            // Re-fetch to see updated data
            onOrgUpdated()
        } catch (err: any) {
            console.error(err);
            setErrorMessage(err.message || "Error updating organization");
        }
    };

    const handlePromoteMembeship = async (membershipId : string) => {
        try {
            await promoteMembership(membershipId);
            // Refresh
            onOrgUpdated();
        } catch (err) {
            console.error('Error creating storage location:', err);
        }
    }

    return (
        <>
            <Button variant={"filled"} onClick={() => setOpened(true)}>Betrieb Bearbeiten</Button>

            <Drawer
                opened={opened}
                onClose={() => setOpened(false)}
                title="Bearbeite Betrieb"
                padding="md"
                size="lg"
            >
                {organization ? (
                    <Flex direction="column" gap="15px" style={{ height: "90vh" }}>
                        <Title order={3} mb="sm">
                            {organization.name}
                        </Title>

                        {/* success/error notifications */}
                        {successMessage && (
                            <Notification
                                icon={<IconCheck size={18} />}
                                color="teal"
                                mb="sm"
                                onClose={() => setSuccessMessage(null)}
                            >
                                {successMessage}
                            </Notification>
                        )}
                        {errorMessage && (
                            <Notification
                                icon={<IconX size={18} />}
                                color="red"
                                mb="sm"
                                onClose={() => setErrorMessage(null)}
                            >
                                {errorMessage}
                            </Notification>
                        )}

                        {/* Mantine Tabs */}
                        <Tabs defaultValue="Details" h={"100%"}>
                            <Tabs.List>
                                <Tabs.Tab value="Details">Details</Tabs.Tab>
                                <Tabs.Tab value="Memberships">Mitglieder</Tabs.Tab>
                            </Tabs.List>

                            {/* Tab 1: Organization Update Form */}
                            <Tabs.Panel value="Details" pt="xs">
                                <Card withBorder p="md" mb="md">
                                    <form onSubmit={orgForm.onSubmit(handleUpdateSubmit)}>
                                        <Text w="bold" mb="xs">
                                            Aktualisiere Betrieb Infos
                                        </Text>

                                        <TextInput
                                            label="Name"
                                            mb="sm"
                                            withAsterisk
                                            {...orgForm.getInputProps("name")}
                                        />
                                        <TextInput
                                            label="Straße"
                                            mb="sm"
                                            withAsterisk
                                            {...orgForm.getInputProps("street")}
                                        />
                                        <TextInput
                                            label="Stadt"
                                            mb="sm"
                                            withAsterisk
                                            {...orgForm.getInputProps("city")}
                                        />
                                        <TextInput
                                            label="Bundesland"
                                            mb="sm"
                                            withAsterisk
                                            {...orgForm.getInputProps("state")}
                                        />
                                        <TextInput
                                            label="Postleitzahl"
                                            mb="sm"
                                            withAsterisk
                                            {...orgForm.getInputProps("zip")}
                                        />
                                        <TextInput
                                            label="Land"
                                            mb="sm"
                                            withAsterisk
                                            {...orgForm.getInputProps("country")}
                                        />

                                        <Group p="right" mt="md">
                                            <Button type="submit">Aktualisieren</Button>
                                        </Group>
                                    </form>
                                </Card>
                            </Tabs.Panel>

                            {/* Tab 2: Memberships / Invitations */}
                            <Tabs.Panel value="Memberships" pt="xs" h={"100%"}>
                                <Flex direction={"column"} justify={"space-between"} h={"100%"} pb={"15px"}>
                                    <Flex direction={"column"}>
                                        <Table highlightOnHover>
                                            <Table.Thead>
                                                <Table.Th>User</Table.Th>
                                                <Table.Th>Rolle</Table.Th>
                                                <Table.Th></Table.Th>
                                            </Table.Thead>
                                            <Table.Tbody>
                                            {organization.memberships.map((membership) => (
                                                <Table.Tr key={membership.id}>
                                                    <Table.Td>{membership.userKey?.username}</Table.Td>
                                                    <Table.Td>{membership.membershipRole}</Table.Td>
                                                    <Table.Td>{
                                                        membership.membershipRole === MembershipRole.MEMBER
                                                            ? <Button variant={"filled"} onClick={() => handlePromoteMembeship(membership.id)}>Befördern</Button>
                                                            : <></>
                                                    }</Table.Td>
                                                </Table.Tr>
                                            ))}
                                            </Table.Tbody>
                                        </Table>
                                    </Flex>

                                    {/* Invitation creation for this org */}
                                    <CreateInvitation organizationId={organization.id} />
                                </Flex>



                            </Tabs.Panel>
                        </Tabs>
                    </Flex>
                ) : (
                    <Text>Kein Betrieb geladen.</Text>
                )}
            </Drawer>
        </>
    );
};
